import { useQuery } from '@apollo/client';
import classnames from 'classnames';
import PropTypes, { string } from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import sortAppliedSchema from '../../analytics-events/sortApplied';
import useCRSParameterListener from '../../hooks/useCRSParameterListener';
import useHandleCategoryParentId from '../../hooks/useHandleCategoryParentId';
import useHandleNavClick from '../../hooks/useHandleNavClick';
import useIsSsr from '../../hooks/useIsSsr';
import useScrollToProduct from '../../hooks/useScrollToProduct';
import useStoreData from '../../hooks/useStoreData';
import useTranslatedText from '../../hooks/useTranslatedText';
import useUpdateTitle from '../../hooks/useUpdateTitle';
import useUrlSync from '../../hooks/useUrlSync';
import { ACTION } from '../../tools/constants';
import getFacetKeysForAnalytics from '../../tools/getFacetKeysForAnalytics';
import getFacetmap from '../../tools/getFacetmap';
import getPageType from '../../tools/getPageType';
import getSelectedFacets from '../../tools/getSelectedFacets';
import manipulateFacetMap from '../../tools/manipulateFacetMap';
import reducer from '../../tools/parametersChange';
import { triggerFilterAnalytics } from '../../tools/triggerAnalytics';
import updateUrl from '../../tools/updateUrl';
import $window from '../../tools/window';
import { ERROR_MESSAGE } from '../Messages/Messages';
import StoreDetailsProvider from '../StoreDetailsProvider/StoreDetailsProvider';
import BreadcrumbSection from './BreadcrumbSection';
import style from './CategoryPage.module.scss';
import CustomerRecommendationsMFE from './CustomerRecommendationsMFE';
import CategoryPreContentHeroEspot from './Espots/CategoryPreContentHeroEspot';
import LeftRail from './LeftRail';
import Main from './Main';
import useEspotNavigationListener from './hooks/useEspotNavigationListener';
import { CATEGORY_PAGE_STATIC_DATA_QUERY, CATEGORY_PAGE_DYNAMIC_DATA_QUERY } from './queries';

function CategoryPage({
  brand,
  categoryId: intlCategoryId,
  facet: intlFacet = [],
  filter: intlFilter = '',
  grouped = false,
  isUnifiedCategoryPage = false,
  kicIds: intlKicIds = '',
  langId = '',
  rows: intlRows = '90',
  sort: intlSort = '',
  start: intlStart = '0',
  storeId = '',
  store = '',
  seqSlot = '',
}) {
  const intlFacetArray = typeof intlFacet === 'string' ? [intlFacet] : intlFacet;
  const [parameters, dispatch] = useReducer(reducer, {
    categoryId: intlCategoryId,
    categoryName: '',
    facet: intlFacetArray,
    filter: intlFilter,
    rows: intlRows,
    sort: intlSort,
    start: intlStart,
    kicIds: intlKicIds,
  });

  const railRef = useRef({ appliedRailFilters: [] });

  const [pageBottomStartParameter, setPageBottomStartParameter] = useState(
    parseInt(parameters.start, 10),
  );

  const storeDetails = useStoreData();

  const {
    data: categoryStaticData,
  } = useQuery(CATEGORY_PAGE_STATIC_DATA_QUERY, {
    variables: {
      // We care about the LaunchDarkly flag only for non-English languages
      useExcludeCategoryHeroEspot: langId !== '-1' && langId !== '-1005',
    },
  });

  const isSSR = useIsSsr();

  const hasShopMyStoreEnabled = categoryStaticData?.config?.hasShopMyStoreEnabled?.value ?? false;

  const excludeCategoryHeroEspot = categoryStaticData?.config?.excludeCategoryHeroEspot?.value
    ?? false;
  const enableXhrNav = categoryStaticData?.config?.enableXhrNav?.value ?? false;

  const titlePageDomain = useTranslatedText('titlePageDomain', { fallback: '' });
  // eslint-disable-next-line max-len
  const hasExposedFiltersAndCategories = categoryStaticData?.config?.enableExposedFiltersAndCategories?.value ?? false;

  const xhrNavClickHandler = useHandleNavClick(dispatch);
  const handleNavClick = (event) => {
    const url = event?.currentTarget?.href || event?.detail?.url;
    const { categoryId } = event?.currentTarget?.dataset || event?.detail;

    if (categoryId && isUnifiedCategoryPage && enableXhrNav) {
      try {
        xhrNavClickHandler(event);
        event.preventDefault();
        return;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('XHR navigation failed, falling back to regular navigation:', error);
      }
    }

    if (url) {
      event.preventDefault();
      window.location.assign(url);
    } else {
      // eslint-disable-next-line no-console
      console.warn('No valid URL found for navigation:', {
        detail: event?.detail,
        dataset: event?.currentTarget?.dataset,
      });
    }
  };

  useEspotNavigationListener(handleNavClick);
  useUrlSync(parameters, dispatch);
  useUpdateTitle(parameters.categoryName ? `${parameters.categoryName} | ${titlePageDomain.value}` : titlePageDomain.value, [parameters.categoryName, titlePageDomain.value]);

  useScrollToProduct();

  const handleStoreFilter = useCallback((storeIdFromFilter) => {
    dispatch({
      type: ACTION.LOCAL_STORE_TOGGLE,
      payload: storeIdFromFilter,
    });
    triggerFilterAnalytics(storeDetails, storeIdFromFilter);
  }, [storeDetails]);

  const handleSortChange = (event) => {
    dispatch({
      type: ACTION.SORT_UPDATE,
      payload: event.target.value,
    });
    if ($window.digitalData) {
      const isNewAnalyticArchitecture = $window.digitalData.get('flag.dep-sort-applied-final-architect') === true;
      if (isNewAnalyticArchitecture) {
        $window.analytics.triggerEvent({
          event_name: 'sort_applied',
          event_type: 'sort',
          search_filter_applied_value_name: event.target.value,
          search_filter_applied_category: 'sort',
          tealium_event: 'sort_applied',
        }, sortAppliedSchema, 'catalog');
      } else {
        $window.digitalData.trigger(`${getPageType()}_sort_applied`, {
          event_name: 'sort_applied',
          event_type: 'sort',
          search_filter_applied_value_name: event.target.value,
          tealium_event: 'sort_applied',
        });
      }
    }
  };

  const onPaginationButtonClick = (event, start) => {
    dispatch({
      type: ACTION.PAGINATION,
      payload: start,
    });
  };

  const onClearAllBtnClick = () => {
    railRef.current.appliedRailFilters = [];
    dispatch({
      type: ACTION.CLEAR_ALL_PARAMETERS,
    });
  };

  /**
   * Dispatches a FACET_TOGGLE Action to update the Facet state with new Facet Map
   * @param{Object} facetMap - Map of selected facets
   */
  const updateFacets = useCallback((facetMap) => {
    dispatch({
      type: ACTION.FACET_TOGGLE,
      payload: [...facetMap],
    });
  }, []);
  /**
     * Handle Facet change from the Bottom Tray on Mobile and right toaster for Desktop
     * calls updateFacets to update the Facet state
     * @param {string} facetId - ID of the facet being changed
     * @param {Array} filters - Array of selected filters
     */
  const handleFacetToasterChange = useCallback((facetId, filters) => {
    const facetMap = getSelectedFacets(parameters.facet);
    if (filters.length === 0) {
      facetMap.delete(facetId);
    } else {
      facetMap.set(facetId, filters);
    }
    updateFacets(facetMap);
  }, [parameters.facet, updateFacets]);

  const handleCheckBoxChange = (event) => {
    const { target: { checked, value } } = event;
    railRef.current.appliedRailFilters = checked
      ? [...railRef.current.appliedRailFilters, value]
      : railRef.current.appliedRailFilters.filter((item) => item !== value);

    const facetMap = getFacetmap(event, parameters.facet);

    dispatch({
      type: ACTION.FACET_TOGGLE,
      payload: [...facetMap],
    });

    if ($window.digitalData) {
      const analyticsEvent = {
        search_filter_applied_category: `${[...getFacetKeysForAnalytics(facetMap)]}`,
        search_filter_applied_value_name: `${[...facetMap.values()]}`,
        event_name: 'filter_applied',
        event_type: 'filters',
        tealium_event: 'filter_applied',
        data_text: 'rail',
        data_action: railRef.current.appliedRailFilters.length > 1 ? 'multiple' : 'single',
      };
      $window.digitalData.trigger('search_filter_applied', analyticsEvent);
    }
  };

  const handleProductData = () => {
    const initialStart = parseInt(parameters.start, 10);
    setPageBottomStartParameter(initialStart);
  };

  const {
    categoryId,
    facet,
    filter,
    kicIds,
    rows,
    sort,
    start,
  } = parameters;

  const {
    data: categoryData,
    previousData: previousCategoryData,
    loading: isCategoryDataLoading,
    error: categoryDataError,
    refetch: categoryDataRefetch,
  } = useQuery(CATEGORY_PAGE_DYNAMIC_DATA_QUERY, {
    variables: {
      categoryId,
      facet,
      filter,
      requestSocialProofData: true,
      rows,
      sort,
      start,
      seqSlot,
      grouped,
      isUnifiedCategoryPage,
      kicIds,
    },
    nextFetchPolicy: 'cache-first',
    onCompleted: () => {
      if (!grouped) {
        handleProductData();
      }
    },
  });

  const { setParentCatIdToDataBridge } = useHandleCategoryParentId();

  useEffect(() => {
    setParentCatIdToDataBridge();
  }, [setParentCatIdToDataBridge]);

  useEffect(() => {
    const handleMarketingFiltersClicked = (event) => {
      const updatedFacetMap = manipulateFacetMap(parameters.facet,
        { ...event?.detail, facetId: event.detail?.facetGenericName });

      dispatch({
        type: ACTION.FACET_TOGGLE,
        payload: [...updatedFacetMap],
      });
    };
    $window.addEventListener('marketingFiltersClicked', handleMarketingFiltersClicked);

    return () => {
      $window.removeEventListener('marketingFiltersClicked', handleMarketingFiltersClicked);
    };
  });

  useEffect(() => {
    if (pageBottomStartParameter > 0
      && pageBottomStartParameter !== parseInt(parameters.start, 10)) {
      updateUrl({
        ...parameters,
        persistHistoryState: true,
        start: pageBottomStartParameter,
      });
    }
    // When parameters change we want start to reset to zero instead of triggering this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageBottomStartParameter]);

  const handleCategoryParametersEvent = useCallback((event) => {
    // if facets are on the only possible event we can get from CRS would be a store filter change

    handleStoreFilter(event.detail.filter ?? '');
  }, [handleStoreFilter]);
  // Need this for backward compatibility
  useCRSParameterListener(handleCategoryParametersEvent);

  if (categoryDataError) {
    console.error('CategoryPage : CATEGORY_PAGE_DYNAMIC_DATA_QUERY : Error executing GraphQL query', {
      categoryIdParameter: intlCategoryId,
    });
    return isSSR ? null : ERROR_MESSAGE;
  }

  let currentData;
  let groupedCategoryData;

  if (isCategoryDataLoading) {
    if (!previousCategoryData) {
      return null;
    }
    currentData = previousCategoryData;
  } else {
    currentData = categoryData;
    if (grouped) {
      groupedCategoryData = categoryData?.groupedCategory;
    }
  }

  const { portrait, landscape, fallback } = categoryData?.category?.marketingBackground || {};
  const hasMarketingBackground = (portrait || landscape || fallback);

  const onClearFacetTag = (payload) => {
    if (railRef.current.appliedRailFilters.includes(`${payload.facetKey},${payload.facetValue}`)) {
      railRef.current.appliedRailFilters = railRef.current.appliedRailFilters.filter(
        (item) => item !== `${payload.facetKey},${payload.facetValue}`,
      );
    }
    const facetMap = getSelectedFacets(parameters.facet);
    // Check if selected checkbox is in already selected facet filters
    if (facetMap.has(payload.facetKey)) {
      const checkedFacets = facetMap.get(payload.facetKey);
      const index = checkedFacets.indexOf(payload.facetValue);
      if (index !== -1) {
        if (checkedFacets.length === 1) {
          facetMap.delete(payload.facetKey);
        } else {
          checkedFacets.splice(index, 1);
        }
      }
    }
    dispatch({
      type: ACTION.FACET_TOGGLE,
      payload: [...facetMap],
    });
  };

  const categoryPageContent = (
    <StoreDetailsProvider brand={brand} store={store} storeId={storeId}>
      {(hasMarketingBackground) ? (
        <style data-testid="marketingBackground">
          {`
            :root {
              ${portrait ? `--marketing-bg-portrait: url(${portrait});` : ''}
              ${landscape ? `--marketing-bg-landscape: url(${landscape});` : ''}
              ${fallback ? `--marketing-bg-fallback: ${fallback};` : ''}
            }
          `}
        </style>
      ) : null}
      {!hasExposedFiltersAndCategories ? (
        <LeftRail
          categoryData={currentData.category}
          dispatch={dispatch}
          enableXhrNav={enableXhrNav}
          handleCheckBoxChange={handleCheckBoxChange}
          handleStoreFilter={handleStoreFilter}
          hasMarketingBackground={!!(hasMarketingBackground)}
          hasShopMyStoreEnabled={hasShopMyStoreEnabled}
          isUnifiedCategoryPage={isUnifiedCategoryPage}
          parameters={parameters}
          storeDetails={storeDetails}
        />
      ) : null}
      <Main
        breadcrumbTrailEntryView={currentData?.category?.breadcrumbTrailEntryView}
        categoryData={currentData.category}
        categoryDataRefetch={categoryDataRefetch}
        excludeCategoryHeroEspot={excludeCategoryHeroEspot}
        fullCatHead={currentData?.category?.fullCatHead}
        grouped={grouped}
        groupedCategoryData={groupedCategoryData}
        handleCheckBoxChange={handleCheckBoxChange}
        handleSortChange={handleSortChange}
        handleStoreFilter={handleStoreFilter}
        hasCatalogMfeEspots={categoryStaticData?.config?.hasCatalogMfeEspots?.value ?? false}
        hasExposedFiltersAndCategories={hasExposedFiltersAndCategories}
        hasShopMyStoreEnabled={hasShopMyStoreEnabled}
        isCategoryDataLoading={isCategoryDataLoading}
        isUnifiedCategoryPage={isUnifiedCategoryPage}
        onClearAllBtnClick={onClearAllBtnClick}
        onClearFacetTag={onClearFacetTag}
        onFacetToasterChange={handleFacetToasterChange}
        onPaginationButtonClick={onPaginationButtonClick}
        parameters={parameters}
        seqSlot={seqSlot}
        sortData={currentData?.category?.sortData}
        storeDetails={storeDetails}
      />
    </StoreDetailsProvider>
  );

  if (isUnifiedCategoryPage) {
    return (
      <>
        <CustomerRecommendationsMFE />
        <div className={classnames(
          'scope-1892',
          { [style.marketingBackground]: (hasMarketingBackground) },
        )}
        >
          <BreadcrumbSection
            brand={brand}
            categoryId={parameters.categoryId}
            espotIdentifier={currentData?.category?.espotIdentifier}
            hasExposedFiltersAndCategories={hasExposedFiltersAndCategories}
          />
          <CategoryPreContentHeroEspot
            brand={brand}
            categoryId={parameters.categoryId}
            espotIdentifier={currentData?.category?.espotIdentifier}
          />
          <div className={style.row}>
            {categoryPageContent}
          </div>
        </div>
      </>
    );
  }
  return categoryPageContent;
}

CategoryPage.propTypes = {
  // Required props
  brand: PropTypes.string.isRequired,
  categoryId: PropTypes.string.isRequired,
  // Optional props
  facet: PropTypes.oneOfType([
    PropTypes.arrayOf(string),
    PropTypes.string,
  ]),
  filter: PropTypes.string,
  grouped: PropTypes.bool,
  isUnifiedCategoryPage: PropTypes.bool,
  kicIds: PropTypes.string,
  langId: PropTypes.string,
  rows: PropTypes.string,
  sort: PropTypes.string,
  start: PropTypes.string,
  storeId: PropTypes.string,
  store: PropTypes.string,
  seqSlot: PropTypes.string,
};

export default CategoryPage;
